import { cast, LocalDate, LocalDateTime, maybe } from "@flockcover/flock-svc-core-types"
import { nullable } from "@flockcover/platform-types-utility"
import * as t from "io-ts"
import { UUID } from "io-ts-types/lib/UUID"

const ProductType = t.union([t.literal(`flat_fee`), t.literal(`usage_based`), t.literal(`rebates`)])
const PolicyStatusType = t.union([
  t.literal(`bound`),
  t.literal(`cancelled`),
  t.literal(`lapsed`),
  t.literal(`void`),
  t.literal(`renewed`),
])

const Premium = t.type({
  net: t.number,
  brokerage: t.number,
  tax: t.number,
  fee: t.number,
  grossDiscount: t.number,
  taxDiscount: t.number,
})

const Deposit = t.type({ rate: t.number, premium: Premium })

const BillingProcess = t.union([
  t.type({ type: t.literal(`bundled`) }),
  t.type({ type: t.literal(`deposit_only`), deposit: Deposit }),
  t.type({ type: t.literal(`distributed`), deposit: Deposit }),
  t.type({ type: t.literal(`premium_finance`), deposit: Deposit, provider: t.string }),
])

const ProductVersion = t.union([
  t.literal(`V1.0-ConnectivityOnlyV1.0`),
  t.literal(`V1.0`),
  t.literal(`V1.1`),
  t.literal(`V2.0-AdmiralV1.0`),
  t.literal(`V2.0-HavenV1.0`),
  t.literal(`V2.0-NIGV1.0`),
  t.literal(`V2.0-RSAV1.0`),
  t.literal(`V2.0`),
])

const PolicyMeta = t.intersection([
  t.type({
    vertical: t.string,
    productType: t.string,
    billingProcess: BillingProcess,
  }),
  t.partial({
    modern: t.boolean,
    fleetId: UUID,
    disableFleetMtas: t.boolean,
    productVersion: ProductVersion,
    segment: t.string,
  }),
])

const PolicyUnderwriter = t.type({
  formalName: t.string,
  name: t.string,
  id: t.string,
})

export const Schedule = t.type({
  id: UUID,
  documentUrl: nullable(t.string),
  // @TODO - there is more properties on a schedule but this is all i need for now
})

const ClaimsAdmin = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    supportsEfnol: t.boolean,
  }),
  t.partial({
    phone: nullable(t.string),
    email: nullable(t.string),
  }),
])

const PolicyType = t.intersection([
  t.type({
    endTime: LocalDateTime,
    id: UUID,
    policyHolder: t.string,
    policyNumber: t.string,
    productType: ProductType,
    startTime: LocalDateTime,
    status: PolicyStatusType,
    totalWrittenPremium: t.number,
    underwriter: PolicyUnderwriter,
    isActive: t.boolean,
    effectiveSchedule: Schedule,
    connectivityRatio: nullable(t.number),
    grossEarnedLossRatio: nullable(t.number),
    netEarnedLossRatio: nullable(t.number),
    claimsFrequency: nullable(t.string),
    averageCappedTimeToReport: nullable(t.number),
    grossEarnedLossRatioAtDates: t.array(t.type({ date: LocalDate, value: t.number })),
  }),
  t.partial({
    coiDocumentUrl: maybe(t.string),
    fnolClaimsAdmin: maybe(ClaimsAdmin),
  }),
])

const MotorPolicy = t.intersection([PolicyType, t.partial({ meta: PolicyMeta })])

const PolicyDocsInput = t.type({
  id: t.string,
})

const PolicyDocsResponse = t.readonlyArray(
  t.type({
    url: t.string,
    key: t.string,
  })
)

export type Policy = t.TypeOf<typeof MotorPolicy>
export type PolicyStatus = t.TypeOf<typeof PolicyStatusType>
export type PolicyDocumentsInput = t.TypeOf<typeof PolicyDocsInput>
export type PolicyDocumentsResponse = t.TypeOf<typeof PolicyDocsResponse>

export const makePolicy = cast(MotorPolicy)
export const Tag = t.union([t.literal(`active`), t.literal(`archived`)])

const PolicyTspsInputType = t.type({
  id: t.string,
})

const TspType = t.type({
  id: t.string,
  name: t.string,
  website: maybe(t.string),
  createdAt: t.string,
  updatedAt: maybe(t.string),
  status: t.partial({ tag: Tag }),
  matrixNames: t.readonlyArray(t.string),
})

const PolicyTspsResponseType = t.readonlyArray(TspType)

export type PolicyTspsInput = t.TypeOf<typeof PolicyTspsInputType>
export type Tsp = t.TypeOf<typeof TspType>
export type TspList = t.TypeOf<typeof PolicyTspsResponseType>
